// Lead Score Updater - Otomatik skor güncellemesi
// Lead etkileşimlerine göre skorları günceller

import { base44 } from '@/api/base44Client';
import { calculateLeadScore, categorizeByScore } from './LeadCategorization';
import { normalizeLeadStatus, isLostStatus } from '@/lib/leadStatus';

/**
 * Tek bir lead'in skorunu günceller
 * @param {string} leadId - Lead ID
 * @returns {Promise<Object>} Güncellenmiş lead
 */
export async function updateLeadScore(leadId) {
  try {
    const [lead, allLeads, orders, tasks] = await Promise.all([
      base44.entities.Lead.get(leadId),
      base44.entities.Lead.list(),
      base44.entities.Order.filter({ lead_id: leadId }),
      base44.entities.Task.filter({ lead_id: leadId }),
    ]);

    const newScore = calculateLeadScore(lead, allLeads, orders, tasks);
    const newCategory = categorizeByScore(newScore);

    // Skoru güncelle
    await base44.entities.Lead.update(leadId, {
      lead_score: newScore,
      priority_category: newCategory,
      score_updated_at: new Date().toISOString(),
    });

    return { ...lead, lead_score: newScore, priority_category: newCategory };
  } catch (error) {
    console.error('Lead score update error:', error);
    throw error;
  }
}

/**
 * Tüm lead skorlarını toplu günceller
 * @param {string} agentEmail - Agent filtresi (opsiyonel)
 * @returns {Promise<number>} Güncellenen lead sayısı
 */
export async function updateAllLeadScores(agentEmail = null) {
  try {
    let leads;
    if (agentEmail) {
      leads = await base44.entities.Lead.filter({ assigned_agent: agentEmail });
    } else {
      leads = await base44.entities.Lead.list();
    }

    const allLeads = await base44.entities.Lead.list();
    let updatedCount = 0;

    for (const lead of leads) {
      try {
        const [orders, tasks] = await Promise.all([
          base44.entities.Order.filter({ lead_id: lead.id }),
          base44.entities.Task.filter({ lead_id: lead.id }),
        ]);

        const newScore = calculateLeadScore(lead, allLeads, orders, tasks);
        const newCategory = categorizeByScore(newScore);

        // Sadece değişiklik varsa güncelle
        if (lead.lead_score !== newScore || lead.priority_category !== newCategory) {
          await base44.entities.Lead.update(lead.id, {
            lead_score: newScore,
            priority_category: newCategory,
            score_updated_at: new Date().toISOString(),
          });
          updatedCount++;
        }
      } catch (err) {
        console.error(`Error updating lead ${lead.id}:`, err);
      }
    }

    return updatedCount;
  } catch (error) {
    console.error('Bulk score update error:', error);
    throw error;
  }
}

/**
 * Aktivite sonrası skor güncelleme
 * Görev tamamlandığında veya sipariş oluşturulduğunda çağrılır
 * @param {string} leadId - Lead ID
 * @param {string} activityType - Aktivite tipi
 * @param {Object} activityData - Aktivite detayları
 */
export async function updateScoreAfterActivity(leadId, activityType, activityData = {}) {
  try {
    let lead;
    try {
      lead = await base44.entities.Lead.get(leadId);
    } catch (e) {
      console.warn(`Lead ${leadId} not found, skipping score update`);
      return null;
    }
    let scoreBonus = 0;

    switch (activityType) {
      case 'task_completed':
        scoreBonus = 5;
        if (activityData.outcome === 'interested') scoreBonus = 15;
        if (activityData.outcome === 'order') scoreBonus = 25;
        if (activityData.outcome === 'callback') scoreBonus = 8;
        break;

      case 'order_created':
        scoreBonus = 20;
        // Bonus based on order value
        if (activityData.orderValue >= 1000) scoreBonus = 30;
        else if (activityData.orderValue >= 500) scoreBonus = 25;
        break;

      case 'quote_sent':
        scoreBonus = 10;
        break;

      case 'quote_accepted':
        scoreBonus = 20;
        break;

      case 'positive_feedback':
        scoreBonus = 8;
        break;

      case 'repeat_order':
        scoreBonus = 15; // Tekrar sipariş: müşteri sadakati
        break;

      case 'negative_feedback':
        scoreBonus = -10;
        break;

      case 'no_response':
        scoreBonus = -3;
        break;

      case 'lost_customer':
        scoreBonus = -20;
        break;

      default:
        scoreBonus = 0;
    }

    const currentScore = lead.lead_score || 50;
    const newScore = Math.max(0, Math.min(100, currentScore + scoreBonus));
    const newCategory = categorizeByScore(newScore);

    await base44.entities.Lead.update(leadId, {
      lead_score: newScore,
      priority_category: newCategory,
      score_updated_at: new Date().toISOString(),
      last_activity: activityType,
      last_activity_date: new Date().toISOString(),
    });

    return { oldScore: currentScore, newScore, category: newCategory };
  } catch (error) {
    console.error('Score update after activity error:', error);
    throw error;
  }
}

/**
 * Belirli bir süre etkileşim olmayan lead'lerin skorunu düşürür
 * @param {number} inactiveDays - Etkileşim olmayan gün sayısı
 * @param {number} penaltyPoints - Düşürülecek puan
 */
export async function penalizeInactiveLeads(inactiveDays = 30, penaltyPoints = 5) {
  try {
    const leads = await base44.entities.Lead.list();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - inactiveDays);

    let penalizedCount = 0;

    for (const lead of leads) {
      // Aktif olmayan lead'leri kontrol et
      if (normalizeLeadStatus(lead.status) !== 'Kunde' && !isLostStatus(lead.status)) {
        const lastActivity = lead.last_activity_date ? new Date(lead.last_activity_date) : null;

        if (!lastActivity || lastActivity < cutoffDate) {
          const currentScore = lead.lead_score || 50;
          const newScore = Math.max(0, currentScore - penaltyPoints);
          const newCategory = categorizeByScore(newScore);

          await base44.entities.Lead.update(lead.id, {
            lead_score: newScore,
            priority_category: newCategory,
            score_updated_at: new Date().toISOString(),
          });
          penalizedCount++;
        }
      }
    }

    return penalizedCount;
  } catch (error) {
    console.error('Inactive penalty error:', error);
    throw error;
  }
}

export default {
  updateLeadScore,
  updateAllLeadScores,
  updateScoreAfterActivity,
  penalizeInactiveLeads,
};
